/* eslint-disable no-unused-expressions */
/**
 * Defines helper functions
 *
 * A collection of helpful functions that may be imported into any js partial.
 *
 * @package Embark
 * @since   1.0.0
 */

/**
 * Append a hash to the current URL.
 *
 * @since 1.0.0
 *
 * @param {String} hash
 */
export function addHashToURL(hash) {
	if (window.history.pushState) {
		const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${hash}`;
		window.history.pushState({ path: newurl }, '', newurl);
	}
}

/**
 * Scroll the document to the provided element.
 *
 * @since 1.0.0
 *
 * @param  {Node}    el The node to check.
 * @param  {Integer} offset Offset from the top of the viewport
 */
export function scrollToEl(el, offset) {
	window.scroll({
		behavior: 'smooth',
		left: 0,
		top: el.offsetTop - offset,
	});
}

/**
 * Check if element is fully visible within the viewport.
 *
 * @since 1.0.0
 *
 * @param  {Node} el The node to check.
 * @return {Boolean}
 */
export function isElementInViewport(el) {
	const rect = el.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= document.documentElement.clientHeight &&
		rect.right <= document.documentElement.clientWidth
	);
}

/**
 * Get header offset with page naviagtion support.
 * @param  object  el  the target element to detect offset height of.
 * @return number  the offset height.
 */
export const headerOffsetHeight = (el) => {
	if (!el) el = document.body;
	const { header, pageNav, breakpoints } = embark.els;
	const pageNavHeight = pageNav?.clientHeight || 0;
	const headerHeight = pageNavHeight + header?.clientHeight;
	// Get the viewport and target element's top position on the page.
	const viewPortTop = el.ownerDocument.defaultView.pageYOffset;
	const elTop =
		el.getBoundingClientRect().top + el.ownerDocument.defaultView.pageYOffset;

	if (header.classList.contains('sticky') === false) {
		return 0;
	}

	if (breakpoints.md.below()) {
		if (elTop < viewPortTop) {
			return header?.clientHeight;
		}
		return 0;
	}

	if (elTop < viewPortTop) {
		return headerHeight;
	}

	return pageNavHeight;
};

/**
 * Check whether the user has access to a mouse or not.
 *
 * @since 2.1.0
 */
export const hasMouse =
	matchMedia('(pointer:fine)').matches ||
	(!!window.MSInputMethodContext && !!document.documentMode);

/**
 * Check the userAgent for common touch devices.
 *
 * @since 1.0.1
 * @since 2.1.0 Use more modern touch detection instead of checking for specific device user agents.
 */
export function isTouchDevice() {
	return (
		!hasMouse &&
		('ontouchstart' in window ||
			navigator.maxTouchPoints > 0 ||
			navigator.msMaxTouchPoints > 0)
	);
}

/**
 * Get the value of a query parameter within a query string.
 *
 * @since 1.2.0
 * @param {string} variable The query param to get.
 */
export function parseQuery(variable) {
	const query = window.location.search.substring(1);
	const vars = query.split('&');
	for (let i = 0; i < vars.length; i++) {
		const pair = vars[i].split('=');
		if (decodeURIComponent(pair[0]) === variable) {
			return decodeURIComponent(pair[1]);
		}
	}

	return false;
}

/**
 * Disable scrolling of the body element without losing your current scroll position.
 */
export function disableScroll() {
	const { documentElement, body } = document;
	if (!documentElement.classList.contains('no-scroll')) {
		const scrollY = window.scrollY || window.pageYOffset;
		body.style.position = 'fixed';
		body.style.top = `-${scrollY}px`;
		documentElement.classList.add('no-scroll');
	}
}

/**
 * Re-enable scrolling while restoring the scroll position to the right point.
 */
export function enableScroll() {
	const { documentElement, body } = document;
	if (documentElement.classList.contains('no-scroll')) {
		const scrollY = body.style.top;
		body.style.position = '';
		body.style.top = '';
		window.scrollTo(0, parseInt(scrollY || '0') * -1);
		documentElement.classList.remove('no-scroll');
	}
}

/**
 * Get the scroll parent for the provided html node.
 *
 * @link https://stackoverflow.com/questions/35939886/find-first-scrollable-parent
 *
 * @since 2.1.1
 * @param {Node} node The html node to find the scroll parent for.
 * @returns {Boolean}
 */
export function getScrollParent(node) {
	if (node == null) {
		return null;
	}

	if (node.scrollHeight > node.clientHeight) {
		return node;
	}

	return getScrollParent(node.parentNode);
}

/**
 * Wrap an element in another element.
 *
 * @link https://plainjs.com/javascript/manipulation/wrap-an-html-structure-around-an-element-28/
 *
 * @since 2.1.1
 * @param {Node} el Node to wrap in wrapper.
 * @param {Node} wrapper Node that wraps el.
 */
export function wrap(el, wrapper) {
	el.parentNode.insertBefore(wrapper, el);
	wrapper.appendChild(el);
}

/**
 * Get Posts from post factory api.
 * @param  {Object} options     Set or override default args and set params.
 * @return {Object}            	returns post factory API response.
 */
export const emFetchPosts = (params = {}) => {
	// Set domain to current domain and endpoint to post factory.
	const { restUrl } = emJsData; // eslint-disable-line
	const apiUrl = `${restUrl}post_factory`;
	let data = false;

	const defaultParams = {
		post_type: 'any',
		posts_per_page: 8,
		term_counts: false,
		partial: 'media-object',
	};

	Object.keys(params).forEach((key) => {
		defaultParams[key] = params[key];
	});

	const fetchPosts = async () => {
		// Loop through the parameters and format for the query string.
		const esc = encodeURIComponent;
		let query = Object.keys(defaultParams).map((k) => {
			if (k === 'post_types') {
				const types = defaultParams[k];
				return `${esc(k)}=${esc(types.split(','))}`;
			}

			return `${esc(k)}=${esc(defaultParams[k])}`;
		});
		query = query ? `?${query.join('&')}` : '';

		// Fetch the data.
		const response = await fetch(apiUrl + query);
		data = await response.json();
		data = data ? JSON.parse(data) : false;

		return data;
	};

	return fetchPosts();
};

/**
 * SlideToggle.
 *
 * @since 2.1.1
 *
 * @param {Node}   el The element to toggle.
 * @param {String} duration The time in seconds in related to css.
 */
export const slideToggle = (el, duration = '.3s') => {
	el.style.transition = `height ${duration} ease`;
	el.style.height =
		el.style.display === 'none' ? '0px' : `${el.clientHeight}px`;
	el.style.overflow = 'hidden';

	/** Slide down. */
	if (el.clientHeight === 0) {
		// Show the element
		el.style.height = 'auto';
		el.style.display = 'block';
		el.style.removeProperty('padding-top');
		el.style.removeProperty('padding-bottom');

		// get elements full height.
		const height = `${el.clientHeight}px`;

		// set height to 0 then to the full height to
		// trigger transition.
		el.style.height = '0px';
		setTimeout(() => {
			el.style.height = height;
			setTimeout(() => {
				el.style.height = 'auto';
			}, 300);
		}, 0);

		/** Slide up. */
	} else {
		/** Set the height as 0px to trigger the slide up animation. */
		el.style.height = '0px';
		el.style.paddingTop = 0;
		el.style.paddingBottom = 0;
	}

	el.addEventListener(
		'transitionend',
		() => {
			if (el.clientHeight === 0) {
				el.style.display = 'none';
			}
		},
		{ once: true },
	);

	window.addEventListener(
		'resize',
		() => {
			if (el.clientHeight > 0) {
				el.style.height = 'auto';
			}
		},
		{ once: true },
	);
};

export const animate = (el, animation, delay = '0s', duration = '0.5s') =>
	new Promise((resolve) => {
		el.style.setProperty('--animate-duration', duration);
		el.style.setProperty('--animate-delay', delay);
		el.style.visibility = 'visible';
		el.classList.add('animated', animation);

		// When the animation ends, we clean the classes and resolve the Promise
		function handleAnimationEnd(event) {
			event.stopPropagation();
			el.classList.remove('animated', animation);
			resolve('Animation ended');
		}

		el.addEventListener('animationend', handleAnimationEnd, { once: true });
	});

// Set a cookie
export const setCookie = (name, value, days) => {
	const expirationDate = new Date();
	expirationDate.setDate(expirationDate.getDate() + days);

	const cookieValue =
		encodeURIComponent(value) +
		(days ? `; expires=${expirationDate.toUTCString()}` : '');

	document.cookie = `${name}=${cookieValue}; path=/`;
};

// Get a cookie
export const getCookie = (name) => {
	const cookies = document.cookie.split(';');

	for (let i = 0; i < cookies.length; i++) {
		const cookie = cookies[i].trim();
		const [cookieName, cookieValue] = cookie.split('=');

		if (cookieName === name) {
			return decodeURIComponent(cookieValue);
		}
	}

	return null;
};

export const deleteCookiesWithPrefix = (prefix) => {
	const cookies = document.cookie.split(';');

	for (let i = 0; i < cookies.length; i++) {
		const cookie = cookies[i].trim();

		if (cookie.startsWith(prefix)) {
			const [cookieName] = cookie.split('=');
			document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		}
	}
};

// add all the elements inside modal which you want to make focusable
export const focusableQuerySelector =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const disableFocusTrap = () => {
	const { el, callback } = window.embark.focusTrap;
	if (el && callback) {
		el.removeEventListener('keyDown', callback);
	}
};

export const enableFocusTrap = (el, setTabIndex = false) => {
	disableFocusTrap();
	if (setTabIndex) {
		el.setAttribute('tabindex', '0');
	}

	const getFocusEls = () => {
		const allFocusEls = el.querySelectorAll(focusableQuerySelector);
		// Iterater through all focusable elements and remove the ones that are hidden.
		const focusEls = Array.from(allFocusEls).filter((e) => {
			// Check if any ancestor has display: none
			let ancestor = e.parentElement;

			while (ancestor) {
				const styles = getComputedStyle(ancestor);
				if (styles.display === 'none') {
					return false; // An ancestor has display: none.
				}
				ancestor = ancestor.parentElement;
			}

			return true; // Include elements whose ancestors don't have display: none.
		});

		const firstFocusEl = focusEls[0] || false;
		const lastFocusEl = focusEls[focusEls.length - 1] || false;

		return {
			focusEls,
			firstFocusEl,
			lastFocusEl,
			hasFirstAndLast: () => firstFocusEl && lastFocusEl,
		};
	};

	const { firstFocusEl } = getFocusEls();

	if (firstFocusEl) {
		firstFocusEl.focus();
	}

	const handleKeyDown = (e) => {
		if (e.key === 'Tab' || e.keyCode === 9) {
			const { activeElement } = document;
			const { firstFocusEl, lastFocusEl, hasFirstAndLast } = getFocusEls();

			if (e.shiftKey) {
				// if shift key pressed for shift + tab combination
				if (hasFirstAndLast() && activeElement === firstFocusEl) {
					lastFocusEl.focus(); // add focus for the last focusable element
					e.preventDefault();
				}
			} else if (hasFirstAndLast() && activeElement === lastFocusEl) {
				// if focused has reached to last focusable element then focus first focusable element after pressing tab
				firstFocusEl.focus(); // add focus for the first focusable element
				e.preventDefault();
			}
		}
	};

	el.addEventListener('keydown', handleKeyDown);

	window.embark.focusTrap = {
		el,
		callback: handleKeyDown,
	};
};
